import React from 'react';
import { Navigate, useSearchParams } from 'react-router-dom';
import { List as StoreItemsList } from '../../supplies/items';
import { List as StoreCategoriesList } from '../store-categories';
import { List as ItemUnitsList } from '../../supplies/item-units';
import { List as SupplyForwardingStatusesList } from '../../supplies/supply-forwarding-statuses';
import { List as SpareItemsList } from '../../spares/spare-items';
import { List as SpareForwardingStatusesList } from '../../spares/spare-forwarding-statuses';
import {
  ReferenceCategoryWorkspace,
  type ReferenceNavItem,
} from './ReferenceCategoryWorkspace';

const BASE = '/parameters/purchasing';

const ITEMS: ReferenceNavItem[] = [
  {
    id: 'supplies-items',
    label: 'Items',
    description: 'Store catalog',
    group: 'Supplies',
    fillHeight: true,
    permissionModuleKey: 'store_items',
  },
  {
    id: 'supplies-categories',
    label: 'Categories',
    description: 'Store item categories',
    group: 'Supplies',
    permissionModuleKey: 'store_categories',
  },
  {
    id: 'supplies-units',
    label: 'Units',
    description: 'Units of measure',
    group: 'Supplies',
    permissionModuleKey: 'store_units',
  },
  {
    id: 'supplies-forwarding-statuses',
    label: 'Forwarding statuses',
    description: 'Supply case forwarding states',
    group: 'Supplies',
    permissionModuleKey: 'supply_forwarding_statuses',
  },
  {
    id: 'spares-items',
    label: 'Items',
    description: 'Spare parts catalog',
    group: 'Spares',
    fillHeight: true,
    permissionModuleKey: 'spare_items',
  },
  {
    id: 'spares-forwarding-statuses',
    label: 'Forwarding statuses',
    description: 'Spare case forwarding states',
    group: 'Spares',
    permissionModuleKey: 'spare_forwarding_statuses',
  },
];

function renderPanel(section: string) {
  switch (section) {
    case 'supplies-items':
      return <StoreItemsList />;
    case 'supplies-categories':
      return <StoreCategoriesList />;
    case 'supplies-units':
      return <ItemUnitsList />;
    case 'supplies-forwarding-statuses':
      return <SupplyForwardingStatusesList />;
    case 'spares-items':
      return <SpareItemsList />;
    case 'spares-forwarding-statuses':
      return <SpareForwardingStatusesList />;
    default:
      return null;
  }
}

const SUPPLIES_SECTIONS: Record<string, string> = {
  items: 'supplies-items',
  categories: 'supplies-categories',
  units: 'supplies-units',
  'forwarding-statuses': 'supplies-forwarding-statuses',
};

const SPARES_SECTIONS: Record<string, string> = {
  items: 'spares-items',
  'forwarding-statuses': 'spares-forwarding-statuses',
};

function catalogRedirect(map: Record<string, string>, fallback: string) {
  return function CatalogRedirect() {
    const [params] = useSearchParams();
    const section = params.get('section') ?? '';
    const next = map[section] ?? fallback;
    return <Navigate to={`${BASE}?section=${next}`} replace />;
  };
}

export const SuppliesCatalogRedirect = catalogRedirect(SUPPLIES_SECTIONS, 'supplies-items');
export const SparesCatalogRedirect = catalogRedirect(SPARES_SECTIONS, 'spares-items');

const PurchasingReferenceWorkspace: React.FC = () => (
  <ReferenceCategoryWorkspace
    basePath={BASE}
    eyebrow="Purchasing"
    items={ITEMS}
    defaultSection="supplies-items"
    renderPanel={renderPanel}
  />
);

export default PurchasingReferenceWorkspace;
